import React, { useState } from 'react';
import axios from '../../config/axios';
import { useApi } from '../lib/api';
import Layout, { TopBar } from '../components/Layout';
import { Meta, Display, Chip, Button } from '../components/Atoms';
import { LoadingState, ErrorState, EmptyState } from '../components/states';
import { confirmDialog } from '../lib/confirm';
import { notifyError, notifySuccess } from '../lib/notify';

const STAGES = [
  { key: 'sown', label: 'Wysiew', tone: 'sage' },
  { key: 'pricked', label: 'Pikowanie', tone: 'sage' },
  { key: 'hardening', label: 'Hartowanie', tone: 'amber' },
  { key: 'planted', label: 'Wysadzone', tone: 'dim' }
];

const SeedlingPipeline = () => {
  const { data, loading, error, refetch } = useApi('/api/seedlings');
  const items = Array.isArray(data?.data) ? data.data : [];

  const [form, setForm] = useState({
    plant_name: '',
    variety: '',
    sown_date: '',
    quantity: '',
    notes: ''
  });
  const [editingId, setEditingId] = useState(null);

  const styles = {
    container: {
      maxWidth: 680,
      margin: '0 auto',
      padding: '0 24px 60px',
      color: 'var(--t-text)',
      fontFamily: 'var(--t-sans)',
      lineHeight: 1.5
    },
    header: {
      marginBottom: 32
    },
    formBox: {
      border: '1px solid var(--t-line)',
      borderRadius: 12,
      padding: 20,
      marginBottom: 24,
      backgroundColor: 'var(--t-bg)'
    },
    inputGroup: {
      marginBottom: 16
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: 6,
      border: '1px solid var(--t-line)',
      backgroundColor: 'var(--t-bg)',
      color: 'var(--t-text)',
      fontSize: 14,
      boxSizing: 'border-box',
      fontFamily: 'inherit'
    },
    label: {
      display: 'block',
      fontFamily: 'var(--t-mono)',
      fontSize: 11,
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
      color: 'var(--t-text-dim)',
      marginBottom: 6
    },
    section: {
      marginBottom: 32
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
      paddingBottom: 8,
      borderBottom: '1px solid var(--t-line)'
    },
    card: {
      border: '1px solid var(--t-line)',
      borderRadius: 8,
      padding: 16,
      marginBottom: 12,
      backgroundColor: 'var(--t-bg-elev)'
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 8
    },
    cardTitle: {
      fontWeight: 600,
      fontSize: 15,
      margin: 0
    },
    cardMeta: {
      fontSize: 13,
      color: 'var(--t-text-dim)',
      marginBottom: 12
    },
    cardActions: {
      display: 'flex',
      gap: 8,
      flexWrap: 'wrap'
    },
    emptySection: {
      padding: 24,
      textAlign: 'center',
      color: 'var(--t-text-mute)',
      fontStyle: 'italic',
      border: '1px dashed var(--t-line)',
      borderRadius: 8
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.plant_name.trim()) {
      notifyError('Nazwa rośliny jest wymagana');
      return;
    }

    try {
      const payload = {
        plant_name: form.plant_name.trim(),
        variety: form.variety.trim() || null,
        sown_date: form.sown_date || null,
        quantity: form.quantity || null,
        notes: form.notes.trim() || null
      };

      if (editingId) {
        await axios.put(`/api/seedlings/${editingId}`, payload);
        notifySuccess('Zaktualizowano rozsadę');
        setEditingId(null);
      } else {
        await axios.post('/api/seedlings', payload);
        notifySuccess('Dodano nową rozsadę');
      }

      setForm({ plant_name: '', variety: '', sown_date: '', quantity: '', notes: '' });
      refetch();
    } catch (err) {
      notifyError('Nie udało się zapisać rozsady');
    }
  };

  const handleEdit = (item) => {
    setForm({
      plant_name: item.plant_name,
      variety: item.variety || '',
      sown_date: item.sown_date || '',
      quantity: item.quantity || '',
      notes: item.notes || ''
    });
    setEditingId(item.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAdvance = async (id) => {
    try {
      await axios.post(`/api/seedlings/${id}/advance`);
      notifySuccess('Przeniesiono do następnego etapu');
      refetch();
    } catch (err) {
      notifyError('Nie udało się przenieść etapu');
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirmDialog({
      title: 'Usuń rozsadę',
      message: 'Czy na pewno chcesz usunąć ten wpis? Tej akcji nie można cofnąć.',
      confirmText: 'Usuń'
    });
    if (!ok) return;

    try {
      await axios.delete(`/api/seedlings/${id}`);
      notifySuccess('Usunięto rozsadę');
      refetch();
    } catch (err) {
      notifyError('Nie udało się usunąć rozsady');
    }
  };

  if (loading) {
    return (
      <Layout topBar={<TopBar left={<Meta>Rozsada</Meta>} />}>
        <div style={styles.container}>
          <LoadingState />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout topBar={<TopBar left={<Meta>Rozsada</Meta>} />}>
        <div style={styles.container}>
          <ErrorState />
        </div>
      </Layout>
    );
  }

  const grouped = STAGES.map((stage) => ({
    ...stage,
    items: items.filter((i) => i.stage === stage.key)
  }));

  return (
    <Layout topBar={<TopBar left={<Meta>Rozsada</Meta>} />}>
      <div style={styles.container}>
        <div style={styles.header}>
          <Meta color="sage">Parapet i taca</Meta>
          <Display size={30} style={{ display: 'block', marginTop: 6 }}>Rozsada</Display>
        </div>

        <div style={styles.formBox}>
          <form onSubmit={handleSubmit}>
            <div style={styles.inputGroup}>
              <label style={styles.label} htmlFor="sp-plant_name">Nazwa rośliny *</label>
              <input
                id="sp-plant_name"
                style={styles.input}
                name="plant_name"
                value={form.plant_name}
                onChange={handleChange}
                placeholder="np. Pomidor"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="sp-variety">Odmiana</label>
                <input
                  id="sp-variety"
                  style={styles.input}
                  name="variety"
                  value={form.variety}
                  onChange={handleChange}
                  placeholder="np. Malinowy"
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="sp-quantity">Ilość</label>
                <input
                  id="sp-quantity"
                  style={styles.input}
                  name="quantity"
                  type="number"
                  value={form.quantity}
                  onChange={handleChange}
                  placeholder="szt."
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="sp-sown_date">Data wysiewu</label>
                <input
                  id="sp-sown_date"
                  style={styles.input}
                  name="sown_date"
                  type="date"
                  value={form.sown_date}
                  onChange={handleChange}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="sp-notes">Notatki</label>
                <input
                  id="sp-notes"
                  style={styles.input}
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="Opcjonalne"
                />
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <Button variant={editingId ? 'amber' : 'primary'} type="submit">
                {editingId ? 'Zaktualizuj' : 'Dodaj rozsadę'}
              </Button>
              {editingId && (
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setForm({ plant_name: '', variety: '', sown_date: '', quantity: '', notes: '' });
                  }}
                  style={{ marginLeft: 8 }}
                >
                  Anuluj
                </Button>
              )}
            </div>
          </form>
        </div>

        {items.length === 0 ? (
          <EmptyState
            title="Brak rozsad"
            description="Dodaj wysiew — poprowadzimy go przez pikowanie, hartowanie aż po wysadzenie."
          />
        ) : (
          grouped.map((stage) => (
            <div key={stage.key} style={styles.section}>
              <div style={styles.sectionHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Meta>{stage.label}</Meta>
                  <Chip tone={stage.tone}>{stage.items.length}</Chip>
                </div>
              </div>

              {stage.items.length === 0 ? (
                <div style={styles.emptySection}>—</div>
              ) : (
                stage.items.map((item) => (
                  <div key={item.id} style={styles.card}>
                    <div style={styles.cardHeader}>
                      <div>
                        <h3 style={styles.cardTitle}>{item.plant_name}</h3>
                        {item.variety && (
                          <div style={{ fontSize: 13, color: 'var(--t-text-dim)' }}>{item.variety}</div>
                        )}
                      </div>
                      <Chip tone={stage.tone} style={{ fontSize: 11 }}>{stage.label}</Chip>
                    </div>
                    <div style={styles.cardMeta}>
                      {item.sown_date ? new Date(item.sown_date).toLocaleDateString('pl-PL') : 'Bez daty'}
                      {item.quantity ? ` • ${item.quantity} szt.` : ''}
                    </div>
                    {item.notes && (
                      <div style={{ fontSize: 13, color: 'var(--t-text-mute)', marginBottom: 12, fontStyle: 'italic' }}>
                        {item.notes}
                      </div>
                    )}
                    <div style={styles.cardActions}>
                      {stage.key !== 'planted' && (
                        <Button variant="amber" size="small" onClick={() => handleAdvance(item.id)}>
                          Przenieś dalej
                        </Button>
                      )}
                      <Button variant="ghost" size="small" onClick={() => handleEdit(item)}>
                        Edytuj
                      </Button>
                      <Button variant="ghost" size="small" onClick={() => handleDelete(item.id)}>
                        Usuń
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          ))
        )}
      </div>
    </Layout>
  );
};

export default SeedlingPipeline;